import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Search,
	X,
	Pill,
	FileText,
	Shirt,
	Dumbbell,
	ChevronRight,
	Grid3X3,
	TrendingUp,
	Eye,
} from 'lucide-react'
import CategoriesEntity from '@/entities/api/categories/category'
import { Entities } from '@/entities/api/entities'
import { entityToSpanish } from '@/utils/entity-convert'

type AllowedEntities = Exclude<
	Entities,
	Entities.CLOTHES | Entities.ACCESSORIES
>

const allowedEntities: AllowedEntities[] = [
	Entities.SUPPLEMENTS,
	Entities.PLANS_PERSONALIZED,
]

interface MegaDropdownProps {
	categories: CategoriesEntity[]
	isOpen: boolean
	onClose: () => void
}

interface CategoryGroup {
	type: AllowedEntities
	categories: CategoriesEntity[]
	title: string
	icon: React.ComponentType<{ className?: string }>
	description: string
	color: string
	isExpanded?: boolean
}

const CATEGORY_CONFIG: Record<
	AllowedEntities,
	{
		title: string
		icon: React.ComponentType<{ className?: string }>
		description: string
		color: string
		accentColor: string
		gradient: string
	}
> = {
	[Entities.SUPPLEMENTS]: {
		title: 'Suplementos',
		icon: Pill,
		description: 'Proteínas, vitaminas y más',
		color:
			'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200/60 hover:from-blue-100 hover:to-blue-150',
		accentColor: 'text-blue-600',
		gradient: 'bg-gradient-to-r from-blue-500 to-blue-600',
	},
	[Entities.PLANS_PERSONALIZED]: {
		title: 'Planes Personalizados',
		icon: FileText,
		description: 'Entrenamientos y dietas',
		color:
			'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200/60 hover:from-emerald-100 hover:to-emerald-150',
		accentColor: 'text-emerald-600',
		gradient: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
	},
	// [Entities.CLOTHES]: {
	// 	title: 'Ropa Deportiva',
	// 	icon: Shirt,
	// 	description: 'Camisetas, shorts y más',
	// 	color:
	// 		'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200/60 hover:from-purple-100 hover:to-purple-150',
	// 	accentColor: 'text-purple-600',
	// 	gradient: 'bg-gradient-to-r from-purple-500 to-purple-600',
	// },
	// [Entities.ACCESSORIES]: {
	// 	title: 'Accesorios',
	// 	icon: Dumbbell,
	// 	description: 'Equipos y herramientas',
	// 	color:
	// 		'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200/60 hover:from-orange-100 hover:to-orange-150',
	// 	accentColor: 'text-orange-600',
	// 	gradient: 'bg-gradient-to-r from-orange-500 to-orange-600',
	// },
}

const MegaDropdown: React.FC<MegaDropdownProps> = ({
	categories,
	isOpen,
	onClose,
}) => {
	const navigate = useNavigate()
	const [searchTerm, setSearchTerm] = useState<string>('')
	const [focusedIndex, setFocusedIndex] = useState<number>(-1)
	const [expandedGroups, setExpandedGroups] = useState<Set<Entities>>(new Set())
	const dropdownRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)

	const INITIAL_ITEMS_PER_GROUP = 20
	const maxItemsPerGroup = searchTerm ? 50 : INITIAL_ITEMS_PER_GROUP

	const groupedCategories = React.useMemo((): CategoryGroup[] => {
		const groups = allowedEntities.map((type) => {
			const typeCategories = categories.filter((cat) => cat.type === type)
			const config = CATEGORY_CONFIG[type]

			return {
				type,
				categories: typeCategories,
				title: config.title,
				icon: config.icon,
				description: config.description,
				color: config.color,
				isExpanded: expandedGroups.has(type),
			}
		})

		return groups.filter((group) => group.categories.length > 0)
	}, [categories, expandedGroups])

	const filteredGroups = React.useMemo((): CategoryGroup[] => {
		if (!searchTerm.trim()) return groupedCategories

		return groupedCategories
			.map((group) => ({
				...group,
				categories: group.categories.filter((cat) =>
					cat.name.toLowerCase().includes(searchTerm.toLowerCase())
				),
			}))
			.filter((group) => group.categories.length > 0)
	}, [groupedCategories, searchTerm])

	const getOptimalLayout = () => {
		const totalGroups = filteredGroups.length
		const hasExpandedGroups = expandedGroups.size > 0

		if (totalGroups <= 2) return 'grid-cols-2'
		if (totalGroups <= 3 && !hasExpandedGroups) return 'grid-cols-3'
		return 'grid-cols-4'
	}

	const toggleGroupExpansion = (groupType: Entities) => {
		setExpandedGroups((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(groupType)) {
				newSet.delete(groupType)
			} else {
				newSet.add(groupType)
			}
			return newSet
		})
	}

	const onCategoryClick = (type: Entities) => {
		navigate(`/Categoria/${entityToSpanish(type)}`)
		onClose()
	}

	const getVisibleCategories = (group: CategoryGroup) => {
		const isExpanded = expandedGroups.has(group.type)
		if (searchTerm || isExpanded) {
			return group.categories
		}
		return group.categories.slice(0, maxItemsPerGroup)
	}

	const hasMoreCategories = (group: CategoryGroup) => {
		return (
			!searchTerm &&
			!expandedGroups.has(group.type) &&
			group.categories.length > maxItemsPerGroup
		)
	}

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const allCategories = filteredGroups.flatMap((group) =>
				getVisibleCategories(group)
			)

			switch (e.key) {
				case 'Escape':
					onClose()
					break
				case 'ArrowDown':
					e.preventDefault()
					setFocusedIndex((prev) => (prev + 1) % allCategories.length)
					break
				case 'ArrowUp':
					e.preventDefault()
					setFocusedIndex((prev) =>
						prev <= 0 ? allCategories.length - 1 : prev - 1
					)
					break
				case 'Enter':
					e.preventDefault()
					if (focusedIndex >= 0 && allCategories[focusedIndex]) {
						onCategoryClick?.(allCategories[focusedIndex].type)
						onClose()
					}
					break
				case 'Tab':
					if (e.shiftKey && focusedIndex <= 0) {
						onClose()
					}
					break
			}
		},
		[filteredGroups, focusedIndex, onClose, onCategoryClick, expandedGroups]
	)

	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			setTimeout(() => searchInputRef.current?.focus(), 150)
		}
		setSearchTerm('')
		setFocusedIndex(-1)
		setExpandedGroups(new Set())
	}, [isOpen])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				onClose()
			}
		}

		const handleEscapeKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			document.addEventListener('keydown', handleEscapeKey)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscapeKey)
		}
	}, [isOpen, onClose])

	if (!isOpen) return null

	const totalCategories = categories.length
	const displayedCategories = filteredGroups.reduce(
		(acc, group) => acc + getVisibleCategories(group).length,
		0
	)

	return (
		<div
			ref={dropdownRef}
			role='menu'
			aria-label='Categorías de productos'
			className='absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-200/80 backdrop-blur-sm z-50 overflow-hidden max-w-7xl mx-auto'
			style={{
				boxShadow:
					'0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 8px 32px -8px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)',
			}}
			onKeyDown={handleKeyDown}
		>
			<div className='bg-gradient-to-r from-gray-50/80 via-white to-red-50/80 backdrop-blur-sm px-6 py-3.5 border-b border-gray-100/80'>
				<div className='flex items-center justify-between mb-3'>
					<div className='flex items-center space-x-2.5'>
						<div className='p-1.5 bg-red-100 rounded-lg'>
							<Grid3X3 className='w-4 h-4 text-red-600' />
						</div>
						<h3 className='text-lg font-bold text-gray-800 tracking-tight'>
							Categorías
						</h3>
						<div className='flex items-center space-x-1'>
							<span className='bg-gradient-to-r from-red-500 to-red-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-sm'>
								{displayedCategories}
							</span>
							{totalCategories !== displayedCategories && (
								<span className='text-xs text-gray-500 font-medium'>
									de {totalCategories}
								</span>
							)}
						</div>
					</div>
					<button
						onClick={onClose}
						className='p-2 cursor-pointer hover:bg-red-50 rounded-xl transition-all duration-200 group hover:scale-105'
						aria-label='Cerrar menú'
					>
						<X className='w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors' />
					</button>
				</div>

				<div className='relative'>
					<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
					<input
						ref={searchInputRef}
						type='text'
						placeholder='Buscar categorías...'
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className='w-full px-4 py-2.5 pl-10 bg-white/90 backdrop-blur-sm border border-gray-300/80 rounded-xl focus:ring-2 focus:ring-red-500/80 focus:border-red-500/80 outline-none transition-all duration-200 text-sm font-medium shadow-sm'
						role='searchbox'
						aria-label='Buscar categorías'
					/>
					{searchTerm && (
						<button
							onClick={() => setSearchTerm('')}
							className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors'
						>
							<X className='w-4 h-4' />
						</button>
					)}
				</div>
			</div>

			<div className='max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300/60 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/60'>
				{filteredGroups.length === 0 ? (
					<div className='px-6 py-16 text-center text-gray-500'>
						<div className='w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center'>
							<Search className='w-8 h-8 text-gray-300' />
						</div>
						<p className='font-semibold text-gray-600 mb-1'>
							No se encontraron categorías
						</p>
						<p className='text-sm text-gray-400'>
							Intenta con otro término de búsqueda
						</p>
					</div>
				) : (
					// <div className={`grid gap-5 p-5 ${getOptimalLayout()}`}>
					// 	{filteredGroups.map((group) => {
					// 		const IconComponent = group.icon
					// 		const config = CATEGORY_CONFIG[group.type]
					// 		const visibleCategories = getVisibleCategories(group)
					// 		const showMoreButton = hasMoreCategories(group)

					// 		return (
					// 			<div key={group.type} className='space-y-3'>
					// 				<button
					// 					onClick={() => onCategoryClick(group.type)}
					// 					className='cursor-pointer'
					// 				>
					// 					<div
					// 						className={`p-4 rounded-xl border-2 shadow-sm ${group.color} transition-all duration-300 hover:shadow-md`}
					// 					>
					// 						<div className='flex items-center space-x-3'>
					// 							<div
					// 								className={`p-2 rounded-lg ${config.gradient} shadow-sm`}
					// 							>
					// 								<IconComponent className='w-4 h-4 text-white' />
					// 							</div>
					// 							<div className='min-w-0 flex-1'>
					// 								<h4 className='font-bold text-gray-800 text-sm truncate leading-tight'>
					// 									{group.title}
					// 								</h4>
					// 								<p className='text-xs text-gray-600/80 truncate mt-0.5'>
					// 									{group.description}
					// 								</p>
					// 							</div>
					// 							<div className='flex items-center space-x-2'>
					// 								<div className='text-right'>
					// 									<div className='flex items-center space-x-1 text-xs text-gray-500'>
					// 										<span className='font-semibold text-gray-700'>
					// 											{group.categories.length}
					// 										</span>
					// 										<TrendingUp className='w-3 h-3 text-gray-400' />
					// 									</div>
					// 									{showMoreButton && (
					// 										<div className='text-xs text-gray-400 mt-0.5'>
					// 											+{group.categories.length - maxItemsPerGroup}{' '}
					// 											más
					// 										</div>
					// 									)}
					// 								</div>
					// 							</div>
					// 						</div>
					// 					</div>
					// 				</button>

					// 				<div className='space-y-1'>
					// 					{visibleCategories.map((category, index) => {
					// 						const globalIndex =
					// 							filteredGroups
					// 								.slice(0, filteredGroups.indexOf(group))
					// 								.reduce(
					// 									(acc, g) => acc + getVisibleCategories(g).length,
					// 									0
					// 								) + index

					// 						return (
					// 							<button
					// 								key={category._id || category.name}
					// 								role='menuitem'
					// 								className={`group cursor-pointer flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
					// 									focusedIndex === globalIndex
					// 										? 'bg-red-50 text-red-700 ring-2 ring-red-200/60 shadow-sm'
					// 										: 'text-gray-700 hover:bg-gray-50/80 hover:text-red-600'
					// 								} hover:shadow-sm`}
					// 								onClick={() => {
					// 									navigate(
					// 										`/SubCategoria/${entityToSpanish(
					// 											category.type
					// 										)}/${category.name.replace(/ /g, '-')}`
					// 									)
					// 									onClose()
					// 								}}
					// 								onMouseEnter={() => setFocusedIndex(globalIndex)}
					// 							>
					// 								<span className='truncate font-medium'>
					// 									{category.name}
					// 								</span>
					// 								<ChevronRight
					// 									className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-all duration-200 flex-shrink-0 ${
					// 										focusedIndex === globalIndex
					// 											? 'opacity-100 text-red-600'
					// 											: 'text-gray-400'
					// 									} group-hover:translate-x-0.5`}
					// 								/>
					// 							</button>
					// 						)
					// 					})}

					// 					{showMoreButton && (
					// 						<button
					// 							onClick={() => toggleGroupExpansion(group.type)}
					// 							className='w-full px-3.5 py-3 text-xs text-gray-600 font-semibold bg-gradient-to-r from-gray-50 to-gray-100 hover:from-red-50 hover:to-red-100 rounded-lg border border-gray-200/60 hover:border-red-200/60 transition-all duration-200 hover:shadow-sm group'
					// 						>
					// 							<div className='flex items-center justify-center space-x-2'>
					// 								<Eye className='w-3.5 h-3.5 text-gray-500 group-hover:text-red-600 transition-colors' />
					// 								<span className='group-hover:text-red-700'>
					// 									Ver {group.categories.length - maxItemsPerGroup} más
					// 									categorías
					// 								</span>
					// 							</div>
					// 						</button>
					// 					)}
					// 				</div>
					// 			</div>
					// 		)
					// 	})}
					// </div>
					<div className='w-full flex flex-col md:flex-row gap-5 p-5'>
						{filteredGroups.map((group) => {
							const IconComponent = group.icon
							const config = CATEGORY_CONFIG[group.type]
							const visibleCategories = getVisibleCategories(group)
							const showMoreButton = hasMoreCategories(group)

							return (
								<div
									key={group.type}
									className='w-full md:flex-1 md:min-w-0 space-y-3'
								>
									<button
										onClick={() => onCategoryClick(group.type)}
										className='w-full cursor-pointer'
									>
										<div
											className={`w-full p-4 rounded-xl border-2 shadow-sm ${group.color} transition-all duration-300 hover:shadow-md`}
										>
											<div className='flex items-center gap-3'>
												<div
													className={`p-2 rounded-lg ${config.gradient} shadow-sm shrink-0`}
												>
													<IconComponent className='w-4 h-4 text-white' />
												</div>

												<div className='min-w-0 flex-1 text-left'>
													<h4 className='font-bold text-gray-800 text-sm truncate leading-tight'>
														{group.title}
													</h4>

													<p className='text-xs text-gray-600/80 truncate mt-0.5'>
														{group.description}
													</p>
												</div>

												<div className='flex items-center gap-2 shrink-0'>
													<div className='text-right'>
														<div className='flex items-center gap-1 text-xs text-gray-500'>
															<span className='font-semibold text-gray-700'>
																{group.categories.length}
															</span>

															<TrendingUp className='w-3 h-3 text-gray-400' />
														</div>

														{showMoreButton && (
															<div className='text-xs text-gray-400 mt-0.5'>
																+{group.categories.length - maxItemsPerGroup}{' '}
																más
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</button>

									<div className='space-y-1'>
										{visibleCategories.map((category, index) => {
											const globalIndex =
												filteredGroups
													.slice(0, filteredGroups.indexOf(group))
													.reduce(
														(acc, g) => acc + getVisibleCategories(g).length,
														0
													) + index

											return (
												<button
													key={category._id || category.name}
													role='menuitem'
													className={`group w-full cursor-pointer flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
														focusedIndex === globalIndex
															? 'bg-red-50 text-red-700 ring-2 ring-red-200/60 shadow-sm'
															: 'text-gray-700 hover:bg-gray-50/80 hover:text-red-600'
													} hover:shadow-sm`}
													onClick={() => {
														navigate(
															`/SubCategoria/${entityToSpanish(
																category.type
															)}/${category.name.replace(/ /g, '-')}`
														)
														onClose()
													}}
													onMouseEnter={() => setFocusedIndex(globalIndex)}
												>
													<span className='truncate font-medium'>
														{category.name}
													</span>

													<ChevronRight
														className={`w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-all duration-200 shrink-0 ${
															focusedIndex === globalIndex
																? 'opacity-100 text-red-600'
																: 'text-gray-400'
														} group-hover:translate-x-0.5`}
													/>
												</button>
											)
										})}

										{showMoreButton && (
											<button
												onClick={() => toggleGroupExpansion(group.type)}
												className='w-full px-3.5 py-3 text-xs text-gray-600 font-semibold bg-gradient-to-r from-gray-50 to-gray-100 hover:from-red-50 hover:to-red-100 rounded-lg border border-gray-200/60 hover:border-red-200/60 transition-all duration-200 hover:shadow-sm group'
											>
												<div className='flex items-center justify-center gap-2'>
													<Eye className='w-3.5 h-3.5 text-gray-500 group-hover:text-red-600 transition-colors' />

													<span className='group-hover:text-red-700'>
														Ver {group.categories.length - maxItemsPerGroup} más
														categorías
													</span>
												</div>
											</button>
										)}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</div>

			<div className='bg-gradient-to-r from-gray-50/50 to-gray-100/50 backdrop-blur-sm px-6 py-3 border-t border-gray-100/80'>
				<div className='flex items-center justify-between text-xs text-gray-600'>
					<div className='flex items-center space-x-2'>
						<div className='flex items-center space-x-1'>
							<span className='w-2 h-2 bg-gradient-to-r from-green-400 to-green-500 rounded-full shadow-sm'></span>
							<span className='font-medium'>
								{totalCategories} categorías disponibles
							</span>
						</div>
						{searchTerm && (
							<div className='text-gray-400'>
								• {displayedCategories} encontradas
							</div>
						)}
					</div>
					<div className='hidden sm:flex items-center space-x-4'>
						<div className='flex items-center space-x-1'>
							<kbd className='px-2 py-1 bg-white border border-gray-300/80 rounded text-xs font-mono shadow-sm'>
								↑↓
							</kbd>
							<span className='text-gray-500'>Navegar</span>
						</div>
						<div className='flex items-center space-x-1'>
							<kbd className='px-2 py-1 bg-white border border-gray-300/80 rounded text-xs font-mono shadow-sm'>
								Enter
							</kbd>
							<span className='text-gray-500'>Seleccionar</span>
						</div>
						<div className='flex items-center space-x-1'>
							<kbd className='px-2 py-1 bg-white border border-gray-300/80 rounded text-xs font-mono shadow-sm'>
								Esc
							</kbd>
							<span className='text-gray-500'>Cerrar</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default MegaDropdown
